import React, { useState, useEffect, useMemo } from 'react';
import { FORM_MODE, Permissions } from '@awp/constants';
import { useFormikContext } from 'formik';
import { Row, Col, Button } from 'react-bootstrap';
import { FormSelect } from '@awp/components/Common/AwpFormInputs';
import Authorize from '@awp/components/Authorize';
import { ActionMenuView, ActionMenuEdit } from '@awp/components/Common/AwpActionMenus';
import Table from '@components/table';
import { sortByNumericThenAlpha } from '@awp/helpers/sorter';
import { isValueEmpty } from '@awp/helpers/utilsReact';

const MODE = {
  SUB_EDIT: 'subedit',
  SUB_VIEW: 'subview',
  SUB_CREATE: 'subcreate',
};

const filterSubAreas = (subAreas, search, statusFilter, avalancheAreaData) => {
  let filtered = avalancheAreaData.value ? subAreas.filter((x) => x.parentId === avalancheAreaData.value) : [];

  if (!isValueEmpty(search)) {
    const searchUpper = search.toUpperCase();
    filtered = filtered.filter(
      (x) => x.name?.toUpperCase().includes(searchUpper) || x.code?.toUpperCase().includes(searchUpper),
    );
  }

  filtered = filterActive(statusFilter, filtered);

  return filtered.sort((a, b) => a.name.localeCompare(b.name));
};

const filterActive = (statusFilter, subAreas) => {
  return subAreas.filter((x) =>
    statusFilter === 'Active' ? !x.isDisabled : statusFilter === 'Inactive' ? x.isDisabled : true,
  );
};

const ActionMenu = ({ row, setSubData, setMode }) => (
  <div>
    <Authorize requires={[Permissions.WriteAdminTools]}>
      <ActionMenuEdit
        onClick={() => {
          setSubData(row.original);
          setMode(MODE.SUB_EDIT);
        }}
      />
    </Authorize>
    <ActionMenuView
      onClick={() => {
        setSubData(row.original);
        setMode(MODE.SUB_VIEW);
      }}
    />
  </div>
);

const StatusFilter = ({ setStatusFilter, initialValue }) => {
  const [selectedStatus, setSelectedStatus] = useState(initialValue || null);
  const { setFieldValue, setTouched } = useFormikContext();

  const handleChange = (option) => {
    const newValue = option?.value || null;
    setFieldValue('statusFilter', newValue);
    setTouched({ statusFilter: false });
    setSelectedStatus(newValue);
    setStatusFilter(newValue);
  };
  return (
    <FormSelect
      className="status-select"
      name="statusFilter"
      label="Status"
      isClearable
      textValue={selectedStatus}
      options={[
        { value: 'Active', label: 'Active' },
        { value: 'Inactive', label: 'Inactive' },
      ]}
      postChange={(e) => {
        setStatusFilter(e?.value || null);
        setSelectedStatus(null);
        handleChange(e);
      }}
    />
  );
};

const AvalancheSubAreasTable = ({ avalancheAreaData, mode, setMode, setSubData, search, subAreas }) => {
  const [statusFilter, setStatusFilter] = useState('Active');
  const [localSubAreas, setLocalSubAreas] = useState(subAreas);

  useEffect(() => {
    setLocalSubAreas(filterSubAreas(subAreas, search, statusFilter, avalancheAreaData));
  }, [search, statusFilter, avalancheAreaData]);

  const columns = useMemo(
    () => [
      { Header: 'Avalanche Sub Area Name', accessor: 'name', sortType: sortByNumericThenAlpha },
      { Header: 'Code', accessor: 'code', sortType: sortByNumericThenAlpha },
      {
        Header: 'Status',
        accessor: 'isDisabled',
        sortType: sortByNumericThenAlpha,
        Cell: ({ row }) => <div className="text-wrap">{row.original.isDisabled ? 'Inactive' : 'Active'}</div>,
      },
      {
        Header: 'Actions',
        accessor: 'Id',
        disableSortBy: true,
        Cell: (props) => <ActionMenu {...props} setSubData={setSubData} setMode={setMode} />,
      },
    ],
    [],
  );

  return (
    <>
      <h4>Avalanche Sub Areas</h4>
      {mode != FORM_MODE.CREATE && (
        <Row>
          <Col sm={4} className="mb-4">
            <StatusFilter setStatusFilter={setStatusFilter} initialValue={{ value: 'Active', label: 'Active' }} />
          </Col>
        </Row>
      )}
      {localSubAreas.length > 0 ? (
        <Table columns={columns} data={localSubAreas} fetchData={() => {}} defaultSortBy="name" />
      ) : (
        <Row>
          <Col sm={12} className="mb-4">
            <p>No records found</p>
          </Col>
        </Row>
      )}

      <div style={{ paddingTop: '1.25rem' }}>
        <Authorize requires={[Permissions.WriteAdminTools]}>
          <Button variant="primary" onClick={() => setMode(MODE.SUB_CREATE)}>
            Create Sub Area
          </Button>
        </Authorize>
      </div>
    </>
  );
};

export default AvalancheSubAreasTable;
